import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AgentLayout from './AgentLayout';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PlacesAutocomplete from '../../components/ui/PlacesAutocomplete';
import { selectCustomerId } from '../../features/auth/authSlice';
import {
  useGetCategoriesQuery,
  usePostAgentPropertyMutation,
} from '../../features/api/apiSlice';
import { useCurrency } from '../../features/currency/CurrencyContext';
import { formatMoney } from '../../lib/currency';
import { isLandCategory } from '../../lib/propertyLabels';

const AgentPropertyNew = () => {
  const navigate = useNavigate();
  const customerId = useSelector(selectCustomerId);
  const { data: categories = [] } = useGetCategoriesQuery();
  const [postProperty, { isLoading }] = usePostAgentPropertyMutation();
  const { usdToUgx, formatPrice, toStorageUgx } = useCurrency();

  const [form, setForm] = useState({
    title: '',
    description: '',
    category_id: '',
    property_type: '0', // 0 sale 1 rent
    price: '',
    price_currency: 'UGX',
    address: '',
    city: '',
    state: '',
    country: 'Uganda',
    latitude: '',
    longitude: '',
    rentduration: 'Monthly',
    package_id: '1',
    status: '1',
    is_luxury: false,
    whatsapp: '',
  });
  const [titleImage, setTitleImage] = useState(null);
  const [titlePreview, setTitlePreview] = useState('');
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    return () => {
      if (titlePreview) URL.revokeObjectURL(titlePreview);
      galleryPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryOptions = [
    { value: '', label: 'Select category' },
    ...(Array.isArray(categories)
      ? categories.map((c) => ({
          value: String(c.id),
          label: c.category || c.name || `Category ${c.id}`,
        }))
      : []),
  ];

  const selectedCategory = Array.isArray(categories)
    ? categories.find((c) => String(c.id) === String(form.category_id))
    : null;
  const landCategory = isLandCategory(selectedCategory);

  const handleTitleImage = (file) => {
    if (titlePreview) URL.revokeObjectURL(titlePreview);
    setTitleImage(file || null);
    setTitlePreview(file ? URL.createObjectURL(file) : '');
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const nextFiles = [...galleryImages, ...files].slice(0, 20);
    galleryPreviews.forEach((url) => URL.revokeObjectURL(url));
    setGalleryImages(nextFiles);
    setGalleryPreviews(nextFiles.map((f) => URL.createObjectURL(f)));
    e.target.value = '';
  };

  const removeGalleryImage = (index) => {
    URL.revokeObjectURL(galleryPreviews[index]);
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.title.trim() || !form.category_id || !form.price) {
      setError('Title, category, and price are required.');
      return;
    }

    const body = new FormData();
    body.append('userid', customerId);
    body.append('title', form.title.trim());
    // Canonical storage currency is UGX
    const priceUgx = toStorageUgx(form.price, form.price_currency || 'UGX');
    if (priceUgx == null || priceUgx <= 0) {
      setError('Enter a valid price.');
      return;
    }
    const descNote =
      form.price_currency === 'USD'
        ? `${form.description.trim() ? form.description.trim() + '\n\n' : ''}[Listed at USD ${Number(
            form.price
          ).toLocaleString(undefined, { maximumFractionDigits: 2 })} · rate 1 USD = ${Number(
            usdToUgx
          ).toLocaleString()} UGX]`
        : form.description.trim();
    body.append('description', descNote);
    body.append('category_id', form.category_id);
    body.append('property_type', form.property_type);
    body.append('price', String(priceUgx));
    body.append('currency', 'UGX');
    body.append('address', form.address);
    body.append('city', form.city);
    body.append('state', form.state);
    body.append('country', form.country);
    body.append('rentduration', form.rentduration);
    body.append('package_id', form.package_id);
    body.append('status', form.status);
    body.append('latitude', form.latitude || '0');
    body.append('longitude', form.longitude || '0');
    body.append('is_luxury', !landCategory && form.is_luxury ? 'true' : 'false');
    if (form.whatsapp.trim()) {
      body.append('whatsapp', form.whatsapp.trim());
    }
    if (titleImage) {
      body.append('title_image', titleImage);
    }
    galleryImages.forEach((file) => {
      body.append('gallery_images[]', file);
    });

    try {
      const res = await postProperty(body).unwrap();
      if (res?.error === true) {
        setError(res.message || 'Could not create property. Check package limits.');
        return;
      }
      setSuccess(res?.message || 'Property submitted successfully.');
      setTimeout(() => navigate('/agent-dashboard/properties'), 1200);
    } catch (err) {
      setError(
        err?.data?.message ||
          err?.message ||
          'Failed to post property. Ensure your agent package allows new properties.'
      );
    }
  };

  return (
    <AgentLayout
      title="Add Property"
      subtitle="Publish a professional property for sale or rent, with cover photo and gallery."
    >
      <form
        onSubmit={handleSubmit}
        className="max-w-3xl rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-5"
      >
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm px-4 py-3">
            {success}
          </div>
        )}

        <Input
          label="Property title"
          required
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="e.g. Modern 3BR apartment in Kololo"
        />

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <textarea
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Highlight location, amenities, and selling points…"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Select
            label="Category"
            options={categoryOptions}
            value={form.category_id}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                category_id: v,
                is_luxury: isLandCategory(
                  (Array.isArray(categories) ? categories : []).find((c) => String(c.id) === String(v))
                )
                  ? false
                  : f.is_luxury,
              }))
            }
            required
          />
          <Select
            label="Transaction"
            options={[
              { value: '0', label: 'For sale' },
              { value: '1', label: 'For rent' },
            ]}
            value={form.property_type}
            onChange={(v) => setForm({ ...form, property_type: v })}
          />
          <Select
            label="Price currency"
            options={[
              { value: 'UGX', label: 'UGX — Ugandan Shilling' },
              { value: 'USD', label: 'USD — US Dollar' },
            ]}
            value={form.price_currency}
            onChange={(v) => setForm({ ...form, price_currency: v })}
          />
          <Input
            label={`Price (${form.price_currency})`}
            type="number"
            required
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            placeholder={form.price_currency === 'USD' ? 'e.g. 2500' : 'e.g. 9500000'}
          />
          {form.property_type === '1' && (
            <Select
              label="Rent duration"
              options={[
                { value: 'Daily', label: 'Daily' },
                { value: 'Monthly', label: 'Monthly' },
                { value: 'Yearly', label: 'Yearly' },
              ]}
              value={form.rentduration}
              onChange={(v) => setForm({ ...form, rentduration: v })}
            />
          )}
        </div>

        {!landCategory ? (
          <label className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!form.is_luxury}
              onChange={(e) => setForm({ ...form, is_luxury: e.target.checked })}
            />
            <span>
              <span className="block text-sm font-semibold text-slate-900">Luxury / premium home</span>
              <span className="block text-xs text-slate-600 mt-0.5">
                Show this listing on the Luxury tab for shoppers looking for high-end homes. Not used for land.
              </span>
            </span>
          </label>
        ) : (
          <p className="text-xs text-slate-500 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            Luxury listing is only for houses and apartments. Land and plots stay on For Sale / For Rent.
          </p>
        )}

        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 space-y-2">
          <Input
            label="WhatsApp number"
            type="tel"
            value={form.whatsapp}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            placeholder="e.g. +256700000000"
          />
          <p className="text-xs text-slate-600">
            Buyers tap WhatsApp on this listing. Leave blank to use your account phone number.
          </p>
        </div>

        {form.price && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <p className="font-medium text-slate-800 mb-1">Price preview</p>
            <p>
              You enter:{' '}
              <strong>
                {formatMoney(form.price, {
                  sourceCurrency: form.price_currency,
                  displayCurrency: form.price_currency,
                  usdToUgx,
                })}
              </strong>
            </p>
            <p className="mt-0.5">
              Stored as UGX:{' '}
              <strong>
                {formatMoney(toStorageUgx(form.price, form.price_currency), {
                  sourceCurrency: 'UGX',
                  displayCurrency: 'UGX',
                  usdToUgx,
                })}
              </strong>
            </p>
            <p className="mt-0.5">
              Shoppers currently see:{' '}
              <strong>{formatPrice(toStorageUgx(form.price, form.price_currency), 'UGX')}</strong>
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Rate: 1 USD = {Number(usdToUgx).toLocaleString()} UGX. Visitors can switch UGX/USD in
              the site header.
            </p>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">Property location</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Search and pick a place — city, region, and map coordinates fill in automatically.
            </p>
          </div>
          <PlacesAutocomplete
            label="Search address or area"
            value={form.address}
            required
            placeholder="e.g. Kololo, Kampala or Plot 12 Acacia Avenue"
            helperText="Type at least 2 characters. Select a result from the list."
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onPlaceSelected={(place) => {
              setForm((f) => ({
                ...f,
                address: place.address || place.formatted_address || f.address,
                city: place.city || f.city,
                state: place.state || f.state,
                country: place.country || f.country || 'Uganda',
                latitude: place.latitude || '',
                longitude: place.longitude || '',
              }));
            }}
          />
          <div className="grid sm:grid-cols-3 gap-4">
            <Input
              label="City"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Kampala"
            />
            <Input
              label="State / region"
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
            />
            <Input
              label="Country"
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Latitude"
              value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              placeholder="From place search"
              readOnly={!!form.latitude}
            />
            <Input
              label="Longitude"
              value={form.longitude}
              onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              placeholder="From place search"
              readOnly={!!form.longitude}
            />
          </div>
          {form.latitude && form.longitude ? (
            <p className="text-xs text-emerald-700 flex items-center gap-1">
              <Icon name="CheckCircle" size={14} />
              Coordinates ready to save: {form.latitude}, {form.longitude}
            </p>
          ) : (
            <p className="text-xs text-amber-700 flex items-center gap-1">
              <Icon name="AlertCircle" size={14} />
              Select a place from search so latitude/longitude are stored on the listing.
            </p>
          )}
        </div>

        <div className="space-y-3 border-t border-slate-100 pt-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cover image</label>
            <p className="text-xs text-slate-500 mb-2">
              Main photo shown on cards and search results.
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleTitleImage(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-900 file:text-white"
            />
            {titlePreview && (
              <div className="mt-3 w-40 h-28 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                <img src={titlePreview} alt="Cover preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Photo gallery</label>
            <p className="text-xs text-slate-500 mb-2">
              Add up to 20 interior and exterior photos. They appear on the property detail page.
            </p>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleGalleryChange}
              className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-amber-500 file:text-slate-950"
            />
            {galleryImages.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-slate-500 mb-2">
                  {galleryImages.length} photo{galleryImages.length === 1 ? '' : 's'} selected
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {galleryPreviews.map((src, index) => (
                    <div
                      key={`${src}-${index}`}
                      className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group"
                    >
                      <img src={src} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(index)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove photo"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button type="submit" loading={isLoading} disabled={isLoading}>
            {isLoading ? 'Publishing…' : 'Publish property'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </form>
    </AgentLayout>
  );
};

export default AgentPropertyNew;
